import type { UseFormReturn, Path, PathValue } from 'react-hook-form'
import type { EventFormDataType } from '@/schemas/eventForm.schema'
import type { EventFormDataType as EventAdminFormDataType } from '@/schemas/eventAdminEventForm.schema'
import { showErrorToast } from '@/components/ui/toast'

export const PDF_MAX_SIZE = 5 * 1024 * 1024 // 5MB
export const PDF_MIME_TYPE = 'application/pdf'

/**
 * Interface for existing PDF document from API
 */
export interface ExistingPdfDocument {
  id?: string
  key: string
  url: string
}

/**
 * Interface for PDF data structure used in forms
 */
export interface EventPDFData {
  id?: string
  key: string
  url?: string
  pdfFile: File
  isNew: boolean
}

/**
 * Validates a PDF file for type and size
 * @param file - The file to validate
 * @returns Object with isValid boolean and optional error message
 */
export const validatePdfFile = (
  file: File
): { isValid: boolean; error?: string } => {
  if (file.type !== PDF_MIME_TYPE) {
    return { isValid: false, error: 'Only PDF files are allowed' }
  }

  if (file.size > PDF_MAX_SIZE) {
    return { isValid: false, error: 'PDF size must be less than 5MB' }
  }

  return { isValid: true }
}

/**
 * Loads existing PDF document data from API response
 * @param document - The document object from API
 * @param getExistingPdfFile - Function to fetch the PDF file from URL
 * @returns EventPDFData object or undefined
 */
export const loadExistingPdfDocument = async (
  document: ExistingPdfDocument | undefined | null,
  getExistingPdfFile: (pdf: {
    key: string
    url: string
  }) => Promise<File | null>
): Promise<EventPDFData | undefined> => {
  if (!document || !document.key || !document.url) {
    return undefined
  }

  const existingPdfFile = await getExistingPdfFile({
    key: document.key,
    url: document.url,
  })

  if (existingPdfFile) {
    return {
      id: document.id,
      key: document.key,
      url: document.url,
      pdfFile: existingPdfFile,
      isNew: false,
    }
  }

  return undefined
}

/**
 * Creates a handler function for selecting a PDF file
 * @param form - The react-hook-form instance
 * @param fieldName - The form field name
 * @returns Handler function for PDF selection
 */
export const createHandleSelectPDF = <
  T extends EventFormDataType | EventAdminFormDataType,
>(
  form: UseFormReturn<T>,
  fieldName: Path<T>
) => {
  return (file: File | null) => {
    if (file) {
      const validation = validatePdfFile(file)
      if (!validation.isValid) {
        showErrorToast(
          validation.error === 'Only PDF files are allowed'
            ? 'Invalid File'
            : 'File Too Large',
          validation.error || 'Invalid file'
        )
        return
      }

      form.setValue(fieldName, {
        key: '',
        pdfFile: file,
        isNew: true,
      } as PathValue<T, Path<T>>)
      form.clearErrors(fieldName)
    }
  }
}

/**
 * Creates a handler function for removing a PDF file
 * @param form - The react-hook-form instance
 * @param fieldName - The form field name
 * @returns Handler function for PDF removal
 */
export const createHandleRemovePDF = <
  T extends EventFormDataType | EventAdminFormDataType,
>(
  form: UseFormReturn<T>,
  fieldName: Path<T>
) => {
  return () => {
    // Set to null to explicitly indicate deletion (different from undefined which means no change)
    form.setValue(fieldName, null as PathValue<T, Path<T>>)
  }
}

/**
 * Prepares PDF document payload for API submission
 * @param eventPDF - The eventPDF form data (null = delete, undefined = no change)
 * @param uploadEventPdf - Function to upload new PDF
 * @returns The document key string,  or undefined (for deletion or no change)
 */
export const preparePdfPayload = async (
  eventPDF:
    | {
        key?: string
        pdfFile?: File
        isNew?: boolean
      }
    | null
    | undefined,
  uploadEventPdf: (file: File) => Promise<string | undefined>
): Promise<string | undefined> => {
  // If explicitly set to null, it means the user deleted the PDF - return undefined
  if (eventPDF === null) {
    return undefined
  }

  // If undefined, no change was made - return undefined to exclude from payload
  if (!eventPDF || !(eventPDF.pdfFile instanceof File)) {
    return undefined
  }

  if (eventPDF.isNew) {
    // Upload new PDF
    const uploadedKey = await uploadEventPdf(eventPDF.pdfFile)
    return uploadedKey
  } else if (eventPDF.key) {
    // Use existing PDF key
    return eventPDF.key
  }

  return undefined
}
